import React, { useState, useEffect } from 'react';
import './EmailConfigurations.css';
import EmailTemplateModal from './EmailTemplateModal';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import
import { toast } from 'react-toastify'; // 2. Import toast for notifications

// Helper to get auth headers
const getAuthHeaders = (isFormData = false) => {
    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`
    };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

const EmailConfigurations = () => {
    const { hasPermission } = usePermissions(); // 3. Get the permission checker function

    // Email Server Configuration State
    const [serverConfig, setServerConfig] = useState({
        host: '',
        port: '',
        username: '',
        password: ''
    });

    // Email Templates State
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load data on component mount
    useEffect(() => {
        // 4. Check for view permission before loading data
        if (hasPermission('EMAIL_CONFIG', 'can_view')) {
            loadEmailConfigurations();
            loadEmailTemplates();
        }
    }, [hasPermission]);

    // Load email server configuration
    const loadEmailConfigurations = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/admin/email-configuration/', {
                headers: getAuthHeaders()  // Add this line
            });
            if (response.ok) {
                const data = await response.json();
                if (data.length > 0) {
                    setServerConfig(data[0]);
                }
            }
        } catch (error) {
            console.error('Error loading email configuration:', error);
        } finally {
            setLoading(false);
        }
    };

    // Load email templates
    const loadEmailTemplates = async () => {
        try {
            const response = await fetch('/api/admin/email-templates/', {
                headers: getAuthHeaders()  // Add this line
            });
            if (response.ok) {
                const data = await response.json();
                setEmailTemplates(data);
            }
        } catch (error) {
            console.error('Error loading email templates:', error);
        }
    };

    // Save email server configuration
    const saveServerConfiguration = async () => {
        // 5. Add permission check
        if (!hasPermission('EMAIL_CONFIG', 'can_edit')) {
            toast.error("You do not have permission to save this configuration.");
            return;
        }
        setSaving(true);
        try {
            const method = serverConfig.id ? 'PUT' : 'POST';
            const url = serverConfig.id 
                ? `/api/admin/email-configuration/${serverConfig.id}/`
                : '/api/admin/email-configuration/';

            const response = await fetch(url, {
                method,
                headers: getAuthHeaders(),  // Add this line
                body: JSON.stringify(serverConfig),
            });

            if (response.ok) {
                const savedConfig = await response.json();
                setServerConfig(savedConfig);
                toast.success('Email server configuration saved successfully!'); // Use toast
            } else {
                throw new Error('Failed to save configuration');
            }
        } catch (error) {
            console.error('Error saving email configuration:', error);
            toast.error('Error saving email configuration'); // Use toast
        } finally {
            setSaving(false);
        }
    };


    // Handle server config input changes
    const handleServerConfigChange = (field, value) => {
        setServerConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Add new email template
    const addNewTemplate = () => {
        // 6. Add permission check
        if (!hasPermission('EMAIL_CONFIG', 'can_create')) {
            toast.error("You do not have permission to add templates.");
            return;
        }
        setEditingTemplate(null);
        setShowTemplateModal(true);
    };

    // Edit existing template
    const editTemplate = (template) => {
        // 7. Add permission check
        if (!hasPermission('EMAIL_CONFIG', 'can_edit')) {
            toast.error("You do not have permission to edit templates.");
            return;
        }
        setEditingTemplate(template);
        setShowTemplateModal(true);
    };

    // Save email template
    const saveEmailTemplate = async (templateData) => {
        // 8. Add permission check
        const isEditing = !!templateData.id;
        const canSave = isEditing ? hasPermission('EMAIL_CONFIG', 'can_edit') : hasPermission('EMAIL_CONFIG', 'can_create');
        if (!canSave) {
            toast.error("You do not have permission to save this template.");
            return;
        }

        try {
            const method = templateData.id ? 'PUT' : 'POST';
            const url = templateData.id 
                ? `/api/admin/email-templates/${templateData.id}/`
                : '/api/admin/email-templates/';

            const response = await fetch(url, {
                method,
                headers: getAuthHeaders(),  // Add this line
                body: JSON.stringify(templateData),
            });

            if (response.ok) {
                await loadEmailTemplates();
                setShowTemplateModal(false);
                setEditingTemplate(null);
                toast.success('Email template saved successfully!'); // Use toast
            } else {
                throw new Error('Failed to save template');
            }
        } catch (error) {
            console.error('Error saving email template:', error);
            toast.error('Error saving email template'); // Use toast
        }
    };

    // Delete email template
    const deleteTemplate = async (templateId) => {
        // 9. Add permission check
        if (!hasPermission('EMAIL_CONFIG', 'can_delete')) {
            toast.error("You do not have permission to delete templates.");
            return;
        }
        if (window.confirm('Are you sure you want to delete this email template?')) {
            try {
                const response = await fetch(`/api/admin/email-templates/${templateId}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()  // Add this line
                });

                if (response.ok) {
                    await loadEmailTemplates();
                    toast.success('Email template deleted successfully!'); // Use toast
                } else {
                    throw new Error('Failed to delete template');
                }
            } catch (error) {
                console.error('Error deleting email template:', error);
                toast.error('Error deleting email template'); // Use toast
            }
        }
    };

    // 10. Top-level permission check for viewing the page
    if (!hasPermission('EMAIL_CONFIG', 'can_view')) {
        return (
            <div className="email-configurations-container">
                <div className="page-header">
                    <h1>Permission Denied</h1>
                    <p>You do not have permission to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="email-configurations-container">
            <div className="page-header">
                <h1>Email Configurations</h1>
                <p>Manage email server settings and email templates</p>
            </div>

            {/* Email Server Configuration Section */}
            <div className="config-section">
                <div className="section-header">
                    <h2>Email Server Configuration</h2>
                    {/* 11. Conditionally render the save button */}
                    {hasPermission('EMAIL_CONFIG', 'can_edit') && (
                        <button 
                            onClick={saveServerConfiguration}
                            disabled={saving}
                            className="btn btn-primary"
                        >
                            {saving ? 'Saving...' : 'Save Configuration'}
                        </button>
                    )}
                </div>

                <div className="server-config-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="host">Host:</label>
                            <input
                                type="text"
                                id="host"
                                value={serverConfig.host}
                                onChange={(e) => handleServerConfigChange('host', e.target.value)}
                                placeholder="smtp.gmail.com"
                                className="form-input"
                                readOnly={!hasPermission('EMAIL_CONFIG', 'can_edit')} // 12. Make read-only
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="port">Port:</label>
                            <input
                                type="number"
                                id="port"
                                value={serverConfig.port}
                                onChange={(e) => handleServerConfigChange('port', e.target.value)}
                                placeholder="587"
                                className="form-input"
                                readOnly={!hasPermission('EMAIL_CONFIG', 'can_edit')} // 13. Make read-only
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="username">Username:</label>
                            <input
                                type="text"
                                id="username"
                                value={serverConfig.username}
                                onChange={(e) => handleServerConfigChange('username', e.target.value)}
                                placeholder="email user"
                                className="form-input"
                                readOnly={!hasPermission('EMAIL_CONFIG', 'can_edit')} // 14. Make read-only
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="password">Password:</label>
                            <input
                                type="password"
                                id="password"
                                value={serverConfig.password}
                                onChange={(e) => handleServerConfigChange('password', e.target.value)}
                                placeholder="Email password"
                                className="form-input"
                                readOnly={!hasPermission('EMAIL_CONFIG', 'can_edit')} // 15. Make read-only
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Email Templates Section */}
            <div className="config-section">
                <div className="section-header">
                    <h2>Email Templates</h2>
                    {/* 16. Conditionally render the add button */}
                    {hasPermission('EMAIL_CONFIG', 'can_create') && (
                        <button 
                            onClick={addNewTemplate}
                            className="btn btn-primary"
                        >
                            Add New Template
                        </button>
                    )}
                </div>

                <div className="templates-container">
                    {emailTemplates.length === 0 ? (
                        <div className="no-templates">
                            <p>No email templates found. Create your first template!</p>
                        </div>
                    ) : (
                        <div className="templates-grid">
                            {emailTemplates.map((template) => (
                                <div key={template.id} className="template-card">
                                    <div className="template-header">
                                        <h3>{template.template_name.toUpperCase()}</h3>
                                        <div className="template-actions">
                                            {/* 17. Conditionally render action buttons */}
                                            {hasPermission('EMAIL_CONFIG', 'can_edit') && (
                                                <button 
                                                    onClick={() => editTemplate(template)}
                                                    className="btn btn-secondary btn-small"
                                                >
                                                    Edit
                                                </button>
                                            )}
                                            {hasPermission('EMAIL_CONFIG', 'can_delete') && (
                                                <button 
                                                    onClick={() => deleteTemplate(template.id)}
                                                    className="btn btn-danger btn-small"
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="template-content">
                                        <div className="template-subject">
                                            <strong>Subject:</strong> {template.subject.toUpperCase()}
                                        </div>
                                        <div className="template-body-preview">
                                            <strong>Body Preview:</strong>
                                            <div 
                                                className="body-preview"
                                                dangerouslySetInnerHTML={{ 
                                                    __html: template.body.substring(0, 100) + '...' 
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Email Template Modal */}
            {showTemplateModal && (
                <EmailTemplateModal
                    isOpen={showTemplateModal}
                    onClose={() => {
                        setShowTemplateModal(false);
                        setEditingTemplate(null);
                    }}
                    onSave={saveEmailTemplate}
                    editTemplate={editingTemplate}
                    hasPermission={hasPermission} // 18. Pass permission to modal
                />
            )}
        </div>
    );
};

export default EmailConfigurations;